// pages/HomePage.jsx
import React from "react";
import { Link } from "react-router-dom";
import {
  Calendar,
  Users,
  Bell,
  MessageSquareLock,
  ShieldCheck,
  Share2,
  Play,
} from "lucide-react";
import HomeHeader from "../modals/HomeHeader.js";
import heroImage from "../assets/illustrations/Doctors-pana.svg";
import patientImage from "../assets/illustrations/Oncology patient-bro.svg";
import doctorImage from "../assets/illustrations/Online Doctor-pana.svg";

const EXPLORE_PATH = "/ProviderSearchPage";
const DEMO_URL = "https://www.youtube.com/watch?v=LSzQzu-PPNU";

export default function HomePage() {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <HomeHeader />

      {/* HERO */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center rounded-full bg-teal-50 border border-teal-200 px-3 py-1 text-xs font-semibold text-teal-700">
              Connecting Hearts, Advancing Care
            </span>

            <h1 className="mt-5 text-5xl font-bold tracking-tight text-gray-900">
              Healthcare access,{" "}
              <span className="text-teal-600">made precise.</span>
            </h1>

            <p className="mt-4 text-lg text-gray-600 leading-relaxed">
              MedMaze connects patients and providers through intelligent
              matching, real-time availability, and seamless communication—so
              finding care feels guided, not overwhelming.
            </p>

            <div className="mt-8 flex items-center gap-4 flex-wrap">
              <Link
                to={EXPLORE_PATH}
                className="bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-full px-6 py-3 text-sm transition-colors"
              >
                Explore how MedMaze works →
              </Link>
              <a
                href={DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 border border-teal-600 text-teal-600 hover:bg-teal-50 font-semibold rounded-full px-6 py-3 text-sm transition-colors"
              >
                <Play size={14} fill="currentColor" /> Watch Demo
              </a>
            </div>
          </div>

          <div className="rounded-2xl overflow-hidden border-2 border-teal-400 shadow-lg">
            <img
              src={heroImage}
              alt="Healthcare provider consulting with patient, illustrating MedMaze's mission to connect patients and providers"
              className="w-full h-80 object-contain bg-gradient-to-br from-teal-50 to-white p-4"
            />
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="bg-gradient-to-br from-teal-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900">About MedMaze</h2>
          <div className="mt-6 space-y-4 text-gray-700 leading-relaxed">
            <p>
              MedMaze was built to address a persistent gap in healthcare:
              patients struggle to find providers who truly fit their needs,
              while providers struggle to be discovered by the right patients at
              the right time.Instead of static directories or endless searching,
              MedMaze introduces a more responsive approach—one that understands
              preferences, timing, and context on both sides. The result is a
              healthcare experience that feels intentional, efficient, and
              human.
            </p>
          </div>
        </div>
      </section>

      {/* CAPABILITIES GRID */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center text-gray-900">
            How MedMaze supports{" "}
            <span className="text-teal-600">better healthcare access</span>
          </h2>
          <p className="mt-3 text-center text-gray-500 max-w-xl mx-auto">
            Connect patients and providers—without unnecessary complexity.
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            <Link
              to="/features/scheduling"
              className="block rounded-2xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-600"
            >
              <CapabilityCard
                icon={Calendar}
                title="Scheduling & Availability"
                body="Make and manage appointments with real-time visibility into provider availability—including cancellations and waitlists."
                badge="Core"
              />
            </Link>
            <Link to="/features/matching" className="block rounded-2xl">
              <CapabilityCard
                icon={Users}
                title="Intelligent Matching"
                body="Connect patients and providers based on meaningful preferences like specialty, location, insurance, and availability."
                badge="Precision"
              />
            </Link>
            <Link to="/features/alerts" className="block rounded-2xl">
              <CapabilityCard
                icon={Bell}
                title="Alerts & Notifications"
                body="Get notified when providers that match your preferences become available—without constantly checking."
                badge="Real-time"
              />
            </Link>
          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-5">
            <Link to="/features/communication" className="block rounded-2xl">
              <CapabilityCard
                icon={MessageSquareLock}
                title="Secure Communication"
                body="Communicate directly and securely between patients and providers—without relying on external tools."
                badge="Secure"
              />
            </Link>
            <CapabilityCard
              icon={ShieldCheck}
              title="Verification & Trust"
              body="Support faster access to care with streamlined verification and trusted provider profiles."
              badge="Trust"
            />
            <Link to="/features/referral" className="block rounded-2xl">
              <CapabilityCard
                icon={Share2}
                title="Referral Coordination"
                body="Structured referral intake, smart specialist matching, and visibility into referral status—replacing fragmented calls and faxes."
                badge="Coordination"
              />
            </Link>
          </div>
        </div>
      </section>

      {/* PATIENT + PROVIDER PANELS */}
      <section className="bg-teal-50 py-16">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <InfoPanel
            title="Designed for Patients"
            body={
              <>
                <p>
                  MedMaze helps patients find care that actually aligns with
                  their needs. Patients can set meaningful preferences—such as
                  specialty, insurance, location, gender, or availability—and
                  let MedMaze work on their behalf.
                </p>
                <p className="mt-3">
                  Patients can favorite providers, receive smart alerts when
                  availability opens up, and get verified quickly through secure
                  insurance validation—making access to care faster and less
                  stressful.
                </p>
                <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4">
                  <p className="text-sm text-gray-700 italic">
                    "Notify me if a neurosurgeon in Boston is available Sunday
                    from 1–4pm."
                  </p>
                </div>
              </>
            }
            illustrationLabel="Semi-realistic patient illustration"
            image={patientImage}
          />

          <InfoPanel
            title="Built for Providers"
            body={
              <>
                <p>
                  MedMaze gives providers visibility and control—without
                  disrupting their workflow.
                </p>
                <p className="mt-3">
                  Providers can manage availability, appointments,
                  cancellations, and waitlists in one place—while being
                  discovered by patients based on real attributes like
                  specialty, location, and schedule.
                </p>
                <p className="mt-3">
                  As profiles become more complete, providers become easier to
                  find, easier to match with, and easier for patients to trust.
                  This isn't about more volume—it's about better alignment.
                </p>
              </>
            }
            illustrationLabel="Semi-realistic provider illustration"
            image={doctorImage}
          />
        </div>
      </section>

      {/* VIDEO GUIDE SECTION */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center text-gray-900">
            Your journey to better health{" "}
            <span className="text-teal-600">starts here</span>
          </h2>
          <p className="mt-4 text-center text-gray-500 max-w-xl mx-auto">
            Getting started is simple. Here's the Guide for Patient and Provider
            Navigation.
          </p>

          {/* Row 1 — text left, video right */}
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-4xl font-bold text-teal-600">01</p>
              <h3 className="mt-2 text-2xl font-bold text-gray-900">
                Finding Care with MedMaze: A Patient&apos;s Walkthrough
              </h3>
              <p className="mt-3 text-gray-600 leading-relaxed">
                Discover how to search, filter, and connect with the right
                provider—step by step.
              </p>
              <a
                href="https://www.youtube.com/watch?v=nyKWKiyspOU"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center text-teal-600 font-semibold text-sm hover:underline"
              >
                Learn more →
              </a>
            </div>
            <div className="rounded-2xl overflow-hidden border-2 border-teal-400 shadow-lg">
              <div className="relative w-full aspect-video">
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/nyKWKiyspOU"
                  title="Finding Care with MedMaze: A Patient's Walkthrough"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </div>

          {/* Row 2 — video left, text right */}
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="rounded-2xl overflow-hidden border-2 border-teal-400 shadow-lg">
              <div className="relative w-full aspect-video">
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/ETgrzRfi0R8"
                  title="Referral Orchestration: A Provider's Guide"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
            <div>
              <p className="text-4xl font-bold text-teal-600">02</p>
              <h3 className="mt-2 text-2xl font-bold text-gray-900">
                Referral Orchestration: A Provider&apos;s Guide
              </h3>
              <p className="mt-3 text-gray-600 leading-relaxed">
                Learn how providers manage referrals, appointments, and patient
                discovery in one place.
              </p>
              <a
                href="https://www.youtube.com/watch?v=ETgrzRfi0R8"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center text-teal-600 font-semibold text-sm hover:underline"
              >
                Learn more →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* COMBINED VISION + CTA */}
      <section className="bg-teal-50 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="max-w-3xl">
              <h2 className="text-3xl font-bold text-gray-900">
                More Than a Platform
              </h2>
              <div className="mt-4 space-y-3 text-gray-700 leading-relaxed">
                <p>
                  MedMaze is an all-in-one solution built to make healthcare
                  access more transparent, efficient, and patient-centered—while
                  giving providers tools to streamline their practice.
                </p>
                <p>
                  By bringing matching, scheduling, verification, and
                  communication into a single experience, MedMaze reduces
                  friction on both sides and creates space for better care to
                  happen.
                </p>
                <div className="flex items-center gap-4 flex-wrap pt-1">
                  <a
                    href="https://docs.google.com/forms/d/1qsFtEfoNHC_lhYgKOjhjIrAJZ5x2rxgpFCtw8zyUKQY"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-teal-700 hover:underline"
                  >
                    Give Feedback as a Provider →
                  </a>
                  <a
                    href="https://docs.google.com/forms/d/1sLnmGfgKje3MGF0j15M0U1AZcXZqm-7My_OV-QUXSlk"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-teal-700 hover:underline"
                  >
                    Give Feedback as a Patient →
                  </a>
                </div>
              </div>
            </div>
            <Link
              to={EXPLORE_PATH}
              className="shrink-0 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-full px-6 py-3 text-sm transition-colors"
            >
              Explore →
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-white py-8 text-xs text-gray-500 text-center border-t">
        © {new Date().getFullYear()} MedMaze. All rights reserved.
      </footer>
    </div>
  );
}

function CapabilityCard({ icon: Icon, title, body, badge }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-5 h-full">
      <div className="flex items-start justify-between gap-4">
        <div className="h-10 w-10 rounded-xl bg-teal-100 flex items-center justify-center">
          <Icon size={22} strokeWidth={1.5} className="text-teal-600" />
        </div>
        <span className="shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold bg-emerald-100 text-emerald-700">
          {badge}
        </span>
      </div>
      <h3 className="mt-4 text-lg font-bold text-gray-900">{title}</h3>
      <p className="mt-2 text-sm text-gray-700 leading-relaxed">{body}</p>
    </div>
  );
}

function InfoPanel({ title, body, illustrationLabel, image }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="p-6 sm:p-7">
        <h3 className="text-xl font-bold text-gray-900">{title}</h3>
        <div className="mt-3 text-sm text-gray-700 leading-relaxed">{body}</div>
      </div>
      <div className="border-t border-gray-200 bg-gradient-to-br from-teal-50 to-white h-44 flex items-center justify-center">
        {image ? (
          <img
            src={image}
            alt={illustrationLabel}
            className="w-full h-full object-contain p-4"
          />
        ) : (
          <div className="text-center px-6">
            <p className="text-sm font-semibold text-gray-800">
              {illustrationLabel}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Swap with image later (semi-realistic)
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
